// =====================================================
// SCHEMAS DE TABLAS DISPONIBLES PARA EL BUILDER
// Define qué tablas y campos están disponibles
// =====================================================

/**
 * Tipo de dato de un campo
 */
export type FieldType = 'number' | 'string' | 'date' | 'boolean'

/**
 * Definición de un campo de tabla
 */
export interface TableField {
  name: string
  label: string
  type: FieldType
  aggregatable: boolean // Si se pueden hacer SUM, AVG, etc
  description?: string
}

/**
 * Schema completo de una tabla (fuente de datos)
 */
export interface DataSourceSchema {
  tableName: string
  label: string
  description: string
  recordCount: number
  fields: TableField[]
  icon?: string,
  groupBy?: any[]
}

/**
 * Catálogo de todas las tablas disponibles para el builder
 */
export const DATA_SOURCES: DataSourceSchema[] = [
  {
    tableName: 'existencias_forestales',
    label: 'Existencias Forestales',
    description: 'Datos de inventario forestal por especie y clase diamétrica',
    recordCount: 6766,
    icon: '🌲',
    fields: [
      {
        name: 'volumen_corteza',
        label: 'Volumen con Corteza (m³)',
        type: 'number',
        aggregatable: true,
        description: 'Volumen de madera con corteza en metros cúbicos'
      },
      {
        name: 'id_municipio',
        label: 'Municipio',
        type: 'number',
        aggregatable: true,
        description: 'Municipio'
      },
      {
        name: 'volumen_sin_corteza',
        label: 'Volumen sin Corteza (m³)',
        type: 'number',
        aggregatable: true,
        description: 'Volumen de madera sin corteza en metros cúbicos'
      },
      {
        name: 'area_basal',
        label: 'Área Basal (m²)',
        type: 'number',
        aggregatable: true,
        description: 'Área basal de los árboles en metros cuadrados'
      },
      {
        name: 'num_pies',
        label: 'Número de Pies',
        type: 'number',
        aggregatable: true,
        description: 'Número total de pies (árboles)'
      },
      {
        name: 'biomasa_aerea',
        label: 'Biomasa Aérea',
        type: 'number',
        aggregatable: true,
        description: 'Biomasa aérea en toneladas'
      },
      {
        name: 'biomasa_raiz',
        label: 'Biomasa Raíz',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'carbono_aereo',
        label: 'Carbono Aéreo',
        type: 'number',
        aggregatable: true,
        description: 'Carbono almacenado en biomasa aérea'
      },
      {
        name: 'carbono_raiz',
        label: 'Carbono Raíz',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'clase_diametrica',
        label: 'Clase Diamétrica (cm)',
        type: 'number',
        aggregatable: false,
        description: 'Clase diamétrica del árbol (10, 15, 20... 70 cm)'
      },
      {
        name: 'codigo_especie',
        label: 'Código de Especie',
        type: 'number',
        aggregatable: false
      },
    ],
    groupBy: [
      {
        table: 'municipios',
        id_local: 'id_municipio',
        id_foreig: 'id_municipio',
      },
      {
        table: 'especies',
        id_local: 'codigo_especie',
        id_foreig: 'codigo_especie',
      }
    ]
  },

  {
    tableName: 'propiedades_forestales',
    label: 'Propiedades Forestales',
    description: 'Información sobre propiedades de bosques y montes',
    recordCount: 128054,
    icon: '🏞️',
    fields: [
      {
        name: 'id_municipio',
        label: 'Municipio',
        type: 'number',
        aggregatable: true,
        description: 'Municipio'
      },
      {
        name: 'superficie_hectareas',
        label: 'Superficie (ha)',
        type: 'number',
        aggregatable: true,
        description: 'Superficie de la propiedad forestal en hectáreas'
      },
      {
        name: 'tipo_propiedad',
        label: 'Tipo de Propiedad',
        type: 'string',
        aggregatable: false,
        description: 'Tipo de propiedad (pública, privada, comunal...)'
      },
      {
        name: 'tipo_administracion',
        label: 'Tipo de Administración',
        type: 'string',
        aggregatable: false
      },
      {
        name: 'titular',
        label: 'Titular',
        type: 'string',
        aggregatable: false
      },
      {
        name: 'numero_monte',
        label: 'Número de Monte',
        type: 'string',
        aggregatable: false
      },
    ],
    groupBy: [
      {
        table: 'municipios',
        id_local: 'id_municipio',
        id_foreig: 'id_municipio',
      }
    ]
  },

  {
    tableName: 'inventarios_ifn2',
    label: 'Inventario Forestal Nacional 2 (IFN2)',
    description: 'Segundo inventario forestal nacional de España',
    recordCount: 1528,
    icon: '📊',
    fields: [
      {
        name: 'id_municipio',
        label: 'Municipio',
        type: 'number',
        aggregatable: true,
        description: 'Municipio'
      },
      {
        name: 'num_pies',
        label: 'Número de Pies',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'area_basal',
        label: 'Área Basal (m²)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'volumen_corteza',
        label: 'Volumen con Corteza (m³)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'volumen_sin_corteza',
        label: 'Volumen sin Corteza (m³)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'volumen_lena',
        label: 'Volumen Leña (m³)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'clase_diametrica',
        label: 'Clase Diamétrica',
        type: 'number',
        aggregatable: false
      },
      {
        name: 'codigo_especie',
        label: 'Código de Especie',
        type: 'number',
        aggregatable: false
      },
    ],
    groupBy: [
      {
        table: 'municipios',
        id_local: 'id_municipio',
        id_foreig: 'id_municipio',
      },
      {
        table: 'especies',
        id_local: 'codigo_especie',
        id_foreig: 'codigo_especie',
      }
    ]
  },

  {
    tableName: 'inventarios_ifn3',
    label: 'Inventario Forestal Nacional 3 (IFN3)',
    description: 'Tercer inventario forestal nacional de España',
    recordCount: 4019,
    icon: '📈',
    fields: [
      {
        name: 'id_municipio',
        label: 'Municipio',
        type: 'number',
        aggregatable: true,
        description: 'Municipio'
      },
      {
        name: 'num_pies',
        label: 'Número de Pies',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'area_basal',
        label: 'Área Basal (m²)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'volumen_corteza',
        label: 'Volumen con Corteza (m³)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'volumen_sin_corteza',
        label: 'Volumen sin Corteza (m³)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'volumen_lena',
        label: 'Volumen Leña (m³)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'clase_diametrica',
        label: 'Clase Diamétrica',
        type: 'number',
        aggregatable: false
      },
      {
        name: 'codigo_especie',
        label: 'Código de Especie',
        type: 'number',
        aggregatable: false
      },
    ],
    groupBy: [
      {
        table: 'municipios',
        id_local: 'id_municipio',
        id_foreig: 'id_municipio',
      },
      {
        table: 'especies',
        id_local: 'codigo_especie',
        id_foreig: 'codigo_especie',
      }
    ]
  },

  {
    tableName: 'valoraciones_economicas',
    label: 'Valoraciones Económicas',
    description: 'Valoración económica de servicios ecosistémicos',
    recordCount: 549,
    icon: '💰',
    fields: [
      {
        name: 'id_municipio',
        label: 'Municipio',
        type: 'number',
        aggregatable: true,
        description: 'Municipio'
      },
      {
        name: 'valor_alimentos',
        label: 'Valor Alimentos (€)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'valor_madera',
        label: 'Valor Madera (€)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'valor_lena',
        label: 'Valor Leña (€)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'valor_pastos',
        label: 'Valor Pastos (€)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'valor_agua',
        label: 'Valor Agua (€)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'valor_recreativo',
        label: 'Valor Recreativo (€)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'valor_caza',
        label: 'Valor Caza (€)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'valor_carbono_suelo',
        label: 'Valor Carbono Suelo (€)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'valor_biodiversidad',
        label: 'Valor Biodiversidad (€)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'area_hectareas',
        label: 'Área (ha)',
        type: 'number',
        aggregatable: true
      },
    ],
    groupBy: [
      {
        table: 'municipios',
        id_local: 'id_municipio',
        id_foreig: 'id_municipio',
      },
      {
        table: 'especies',
        id_local: 'codigo_especie',
        id_foreig: 'codigo_especie',
      }
    ]
  },

  {
    tableName: 'modelos_combustible',
    label: 'Modelos de Combustible',
    description: 'Modelos de combustible forestal y riesgo de incendios',
    recordCount: 628,
    icon: '🔥',
    fields: [
      {
        name: 'id_municipio',
        label: 'Municipio',
        type: 'number',
        aggregatable: true,
        description: 'Municipio'
      },
      {
        name: 'superficie_hectareas',
        label: 'Superficie (ha)',
        type: 'number',
        aggregatable: true
      },
      {
        name: 'modelo_combustible',
        label: 'Modelo de Combustible',
        type: 'string',
        aggregatable: false,
        description: 'Código del modelo de combustible'
      },
      {
        name: 'riesgo_incendio',
        label: 'Riesgo de Incendio',
        type: 'string',
        aggregatable: false,
        description: 'Nivel de riesgo: Bajo, Medio, Alto, Muy Alto'
      },
    ],
    groupBy: [
      {
        table: 'municipios',
        id_local: 'id_municipio',
        id_foreig: 'id_municipio',
      }
    ]
  },

  {
    tableName: 'calidades_forestales',
    label: 'Calidades Forestales',
    description: 'Calidad de estaciones forestales por municipio',
    recordCount: 206,
    icon: '🌳',
    fields: [
      {
        name: 'id_municipio',
        label: 'Municipio',
        type: 'number',
        aggregatable: true,
        description: 'Municipio'
      },
      {
        name: 'volumen_medio',
        label: 'Volumen Medio (m³/ha)',
        type: 'number',
        aggregatable: true,
        description: 'Volumen con corteza en metros cúbicos por hectárea'
      },
      {
        name: 'calidad_estacion',
        label: 'Calidad de Estación',
        type: 'string',
        aggregatable: false
      },
      {
        name: 'codigo_especie',
        label: 'Código de Especie',
        type: 'number',
        aggregatable: false
      },
    ],
    groupBy: [
      {
        table: 'municipios',
        id_local: 'id_municipio',
        id_foreig: 'id_municipio',
      },
      {
        table: 'especies',
        id_local: 'codigo_especie',
        id_foreig: 'codigo_especie',
      }
    ]
  },

  {
    tableName: 'municipios',
    label: 'Municipios de Castilla y León',
    description: 'Datos ampliados de municipios con coordenadas GPS, población y más',
    recordCount: 2248,
    icon: '🏘️',
    fields: [
      {
        name: 'codigo_ine',
        label: 'Código INE',
        type: 'string',
        aggregatable: false,
        description: 'Código único del municipio'
      },
      {
        name: 'nombre',
        label: 'Nombre del Municipio',
        type: 'string',
        aggregatable: false
      },
      {
        name: 'provincia',
        label: 'Provincia',
        type: 'string',
        aggregatable: false
      },
      {
        name: 'poblacion',
        label: 'Población',
        type: 'number',
        aggregatable: true,
        description: 'Número de habitantes'
      },
      {
        name: 'superficie',
        label: 'Superficie (km²)',
        type: 'number',
        aggregatable: true,
        description: 'Superficie total en kilómetros cuadrados'
      },
      {
        name: 'latitud',
        label: 'Latitud',
        type: 'number',
        aggregatable: false,
        description: 'Coordenada de latitud GPS'
      },
      {
        name: 'longitud',
        label: 'Longitud',
        type: 'number',
        aggregatable: false,
        description: 'Coordenada de longitud GPS'
      },
      {
        name: 'altitud',
        label: 'Altitud (m)',
        type: 'number',
        aggregatable: true,
        description: 'Altitud sobre el nivel del mar en metros'
      },
    ]
  },
]

/**
 * Obtener schema de una tabla por nombre
 */
export function getDataSource(tableName: string): DataSourceSchema | undefined {
  return DATA_SOURCES.find(ds => ds.tableName === tableName)
}

/**
 * Obtener solo campos agregables de una tabla
 */
export function getAggregatableFields(tableName: string): TableField[] {
  const ds = getDataSource(tableName)
  return ds?.fields.filter(f => f.aggregatable) || []
}

/**
 * Obtener todos los campos de una tabla
 */
export function getAllFields(tableName: string): TableField[] {
  const ds = getDataSource(tableName)
  return ds?.fields || []
}

/**
 * Iconos disponibles para KPIs
 */
export const AVAILABLE_ICONS = [
  '🌲', '🌳', '🌿', '🍃', '🌾',
  '💧', '🔥', '📊', '📈', '🏞️',
  '🌍', '🌲', '⛰️', '🦌', '🐦'
]

/**
 * Colores disponibles con sus labels
 */
export const AVAILABLE_COLORS = [
  { value: 'green', label: 'Verde Forestal', bg: 'bg-green-500' },
  { value: 'blue', label: 'Azul Hídrico', bg: 'bg-blue-500' },
  { value: 'orange', label: 'Naranja Fuego', bg: 'bg-orange-500' },
  { value: 'red', label: 'Rojo Alerta', bg: 'bg-red-500' },
] as const

/**
 * Tipos de agregación con labels
 */
export const AGGREGATION_TYPES = [
  { value: 'SUM', label: 'Suma Total', icon: '∑', description: 'Suma de todos los valores' },
  { value: 'AVG', label: 'Promedio', icon: '≈', description: 'Promedio aritmético' },
  { value: 'COUNT', label: 'Contar', icon: '#', description: 'Número de registros' },
  { value: 'MAX', label: 'Máximo', icon: '↑', description: 'Valor máximo' },
  { value: 'MIN', label: 'Mínimo', icon: '↓', description: 'Valor mínimo' },
] as const
